import { useState } from 'react'
import { Button, MenuItem, TextField, Typography } from '@mui/material'
import PropTypes from 'prop-types'
import { apiPost } from '../../generic/Api_Functions'
import { SmartDialog } from '../../generic/utilities/SmartDialog'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'
import { buildResyncAlert } from './resyncResultAlert'

/**
 * Route the resync of ONE fabrication order goes through (`FabricationOrderAlignmentRest:79`,
 * verified in Sun on 2026-09-09). Same permission as part 1's send -- it is the same act of pushing
 * this node's version outwards (RN40/V16) -- and it answers 200 with a `SyncOutcomeDto`, 400 with
 * a stable `{ code, message }`, or 403.
 */
export const FABRICATION_ORDER_RESYNC_ENDPOINT = 'cluster/fabricationOrderResync'

/**
 * S69/S70/S102 (historia 108, CU5/CU17): resynchronises ONE fabrication order with ONE cluster node.
 *
 * P2-D4 -- deliberately NOT a bulk action. Part 1's send is the one that works on a selection; a
 * resync repairs a specific divergence somebody is looking at, so it lives on the row.
 *
 * ⚠️ **The 200 carries the OUTCOME**, in the same `SyncOutcomeDto` the bulk send of part 1 answers
 * with: `DELIVERED`, `REJECTED` with `RESYNC_DEGRADED` when only the header could be
 * applied, or `NOT_DELIVERED` when the node is not answering and the queue took it. Sun delivers it
 * DIRECT first and falls back to the queue precisely so this dialog can tell a person what happened
 * (`StandardFabricationOrderResyncService:95-99`). The row is refreshed anyway: a queued resync
 * finishes later, and the indicator is where that shows up.
 *
 * @param {Object} props Props object
 * @param {Object} props.row Fabrication order to resynchronise
 * @param {string[]} props.nodes Candidate nodes, current one already removed (DG-18)
 * @param {Function} props.showAlert Alert callback of the fabrication order window
 * @param {Function} props.onClose Called when the dialog is dismissed
 * @param {Function} props.onResynced Called after a queued resync, so the row is read again
 * @returns {JSX.Element} The confirmation dialog of the resync
 */
const FabricationOrderResyncAction = ({ row, nodes, showAlert, onClose, onResynced }) => {
  const { t_ } = useSmartTranslation()
  // The scope is plant + ONE cell (02-reglas.md:626-628), so there is normally a single candidate
  // and making the user pick the only option would be ceremony. With more than one, they choose.
  const [targetNode, setTargetNode] = useState(nodes.length === 1 ? nodes[0] : '')

  const orderCode = row?.fabricationOrder || ''

  const resync = async () => {
    try {
      const outcome = await apiPost(FABRICATION_ORDER_RESYNC_ENDPOINT, {
        fabricationOrderCode: orderCode,
        targetNodeName: targetNode
      })
      showAlert(buildResyncAlert(outcome, t_))
      // Se recarga SIEMPRE, incluso tras un desenlace parcial o encolado: el indicador de la fila es
      // donde se ve cómo queda, y dejarla con el valor viejo contradiría a la propia alerta.
      onResynced()
    } catch (error) {
      // The body of a 400 is `{ code, message }`: `code` is the stable i18n key and comes FIRST.
      // `Api_Functions` copies Sun's `message` into `error.message`, and Sun marks that text "Not for
      // display" -- preferring it showed developer English to the user (JUP-2 of the Judge). The
      // message is only a last resort for errors that carry no code at all (network, 5xx).
      const reason = error?.data?.code || error?.message || ''
      showAlert({
        severity: 'error',
        title: t_('The resynchronisation was refused'),
        message: reason === '' ? '' : t_(reason)
      })
    }
    onClose()
  }

  return (
    <SmartDialog
      setOpen
      title='default'
      autoClose={false}
      accept={false}
      cancel
      cancelCallback={onClose}
      renderComponent={nodes.length > 1
        ? (
          <TextField
            select
            fullWidth
            value={targetNode}
            label={t_('Select destination node')}
            helperText={targetNode === '' ? t_('Destination node is required') : ''}
            onChange={(event) => setTargetNode(event.target.value)}
          >
            {nodes.map((node) => (
              <MenuItem key={node} value={node}>{node}</MenuItem>
            ))}
          </TextField>
          )
        : undefined}
      message={t_('You are going to resynchronise {{code}} with the other node', { code: orderCode })}
      renderCustomButtons={
        // Only the confirmation is disabled while there is no node: `disableButtons` of SmartDialog
        // would disable cancel too, and leaving a person unable to back out of a dialog is worse
        // than letting them confirm nothing. Same pattern as part 1's send.
        <Button color='primary' variant='outlined' disabled={targetNode === ''} onClick={resync}>
          <Typography>{t_('ACCEPT')}</Typography>
        </Button>
      }
    />
  )
}

FabricationOrderResyncAction.propTypes = {
  row: PropTypes.object,
  nodes: PropTypes.array.isRequired,
  showAlert: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onResynced: PropTypes.func.isRequired
}

export default FabricationOrderResyncAction
